// src/pages/DashboardPage.js
import { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';
import { useUserDoc } from '../hooks/useUserDoc';
import {
  getSavedAgreements, getUserDoc, saveAgreementToUser,
  archiveAgreement, restoreAgreement, removeSavedAgreement
} from '../firebase/services';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase/config';
import CircleIcon, { SvgIcon } from '../components/Icon';
import Navbar from '../components/Navbar';
import { FULL_ACCESS_MODE } from '../config/flags';

const StatusBadge = ({ status, archived, t }) => {
  if (status === 'Cancelled') return <span className="badge" style={{ background: 'var(--border)', color: 'var(--muted)' }}>{t('dashboard.cancelled')}</span>;
  if (archived) return <span className="badge" style={{ background: 'var(--border)', color: 'var(--muted)' }}>{t('dashboard.archived')}</span>;
  if (status === 'Confirmed') return <span className="badge badge-confirmed">{t('dashboard.confirmed')}</span>;
  if (status === 'Declined') return <span className="badge badge-declined">{t('dashboard.declined')}</span>;
  return <span className="badge badge-pending">{t('dashboard.pending')}</span>;
};

const AgreementCard = ({ agreement, onView, onArchive, onRestore, isArchived, t }) => (
  <div className="agreement-card">
    <div className="agreement-card-header">
      <div className="agreement-title">
        {agreement.title}
        {agreement.verified && <SvgIcon name="shield" size={12} color="var(--confirmed)" strokeWidth={2.5} style={{ marginLeft: 4, verticalAlign: 'middle' }}/>}
        {agreement.evidence?.length > 0 && <span className="evidence-badge"><SvgIcon name="paperclip" size={10} color="#6366F1" strokeWidth={2.5}/> {agreement.evidence.length}</span>}
      </div>
      <StatusBadge status={agreement.status} archived={isArchived} t={t} />
    </div>
    <div className="agreement-meta">
      {t('dashboard.with')} <strong>{agreement.partnerName || 'group'}</strong>
      {agreement.createdTime && ` · ${new Date(agreement.createdTime?.toDate?.() || agreement.createdTime).toLocaleDateString()}`}
    </div>
    {agreement.amount && (
      <div className="agreement-amount">{agreement.currency} {agreement.amount}</div>
    )}
    <div className="agreement-detail">
      {agreement.details?.slice(0, 90)}{agreement.details?.length > 90 ? '...' : ''}
    </div>
    <div className="agreement-actions">
      <button className="btn btn-secondary btn-sm" onClick={() => onView(agreement)}>{t('dashboard.view')}</button>
      {!isArchived && <button className="btn btn-secondary btn-sm" onClick={() => onArchive(agreement.id)}>{t('dashboard.archive')}</button>}
      {isArchived && <button className="btn btn-secondary btn-sm" onClick={() => onRestore(agreement.id)}>{t('dashboard.restore')}</button>}
    </div>
  </div>
);

const SavedCard = ({ agreement, onView, onRemove, t }) => (
  <div className="agreement-card">
    <div className="agreement-card-header">
      <div className="agreement-title">{agreement.title}</div>
      <StatusBadge status={agreement.status} t={t} />
    </div>
    <div className="agreement-meta">{t('dashboard.between')} <strong>{agreement.createdByName}</strong> and <strong>{agreement.partnerName}</strong></div>
    {agreement.amount && (
      <div className="agreement-amount">{agreement.currency} {agreement.amount}</div>
    )}
    <div className="agreement-detail">
      {agreement.details?.slice(0, 90)}{agreement.details?.length > 90 ? '...' : ''}
    </div>
    <div className="agreement-actions">
      <button className="btn btn-secondary btn-sm" onClick={() => onView(agreement)}>{t('dashboard.view')}</button>
      <button className="btn btn-danger btn-sm" onClick={() => onRemove(agreement.agreementID)}>{t('dashboard.remove')}</button>
    </div>
  </div>
);

const SharedCard = ({ agreement, onView, t }) => (
  <div className="agreement-card">
    <div className="agreement-card-header">
      <div className="agreement-title">
        {agreement.title}
        {agreement.verified && <SvgIcon name="shield" size={12} color="var(--confirmed)" strokeWidth={2.5} style={{ marginLeft: 4, verticalAlign: 'middle' }}/>}
      </div>
      <StatusBadge status={agreement.status} t={t} />
    </div>
    <div className="agreement-meta">{t('dashboard.with')} <strong>{agreement.createdByName}</strong></div>
    {agreement.amount && (
      <div className="agreement-amount">{agreement.currency} {agreement.amount}</div>
    )}
    <div className="agreement-detail">
      {agreement.details?.slice(0, 90)}{agreement.details?.length > 90 ? '...' : ''}
    </div>
    <div className="agreement-actions">
      <button className="btn btn-secondary btn-sm" onClick={() => onView(agreement)}>{t('dashboard.view')}</button>
    </div>
  </div>
);

const SectionToggle = ({ icon, title, subtitle, count, expanded, onToggle, children }) => (
  <>
    <div
      className="section-header"
      onClick={onToggle}
      style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', userSelect: 'none' }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        {icon}
        <div>
          <div className="section-title">{title} {count > 0 && <span style={{ fontSize: '0.78rem', color: 'var(--muted)', fontWeight: 400 }}>({count})</span>}</div>
          {subtitle && <div className="section-subtitle">{subtitle}</div>}
        </div>
      </div>
      <span style={{ fontSize: '0.9rem', color: 'var(--muted)', transition: 'transform 0.2s', transform: expanded ? 'rotate(0deg)' : 'rotate(-90deg)' }}>&#9660;</span>
    </div>
    {expanded && children}
  </>
);

export default function DashboardPage() {
  const { user } = useAuth();
  const { isPremium, reload: reloadUser } = useUserDoc();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [agreements, setAgreements] = useState([]);
  const [shared, setShared] = useState([]);
  const [saved, setSaved] = useState([]);
  const [loading, setLoading] = useState(true);
  const [upgradeBanner, setUpgradeBanner] = useState(null);
  const { t } = useTranslation();

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [paymentsOnly, setPaymentsOnly] = useState(false);
  const [verifiedOnly, setVerifiedOnly] = useState(false);
  const [sortBy, setSortBy] = useState('recent');

  // Handle ?upgraded=premium or ?upgraded=business from Stripe redirect
  useEffect(() => {
    const upgraded = searchParams.get('upgraded');
    if (upgraded) {
      setUpgradeBanner(upgraded);
      if (reloadUser) reloadUser();
      searchParams.delete('upgraded');
      setSearchParams(searchParams, { replace: true });
      setTimeout(() => setUpgradeBanner(null), 8000);
    }
  }, [searchParams, setSearchParams, reloadUser]);

  const [sections, setSections] = useState({
    pending: true, active: true, declined: true, saved: true, shared: true, archived: true,
  });
  const toggle = (key) => setSections(prev => ({ ...prev, [key]: !prev[key] }));

  const load = useCallback(async () => {
    const pendingID = localStorage.getItem('pendingSaveAgreementID');
    if (pendingID) {
      try {
        await saveAgreementToUser(user.uid, pendingID);
        localStorage.removeItem('pendingSaveAgreementID');
      } catch (err) {
        console.error('[PendingSave] Failed:', err);
        localStorage.removeItem('pendingSaveAgreementID');
      }
    }
    const q = query(collection(db, 'agreements'), where('createdByUID', '==', user.uid));
    const snap = await getDocs(q);
    setAgreements(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    try {
      const sharedQ = query(collection(db, 'agreements'), where('confirmedByUid', '==', user.uid));
      const sharedSnap = await getDocs(sharedQ);
      setShared(sharedSnap.docs.map(d => ({ id: d.id, ...d.data() })).filter(a => a.createdByUID !== user.uid));
    } catch (err) {
      console.error('[Dashboard] shared query failed:', err);
      setShared([]);
    }
    const userDoc = await getUserDoc(user.uid);
    if (userDoc?.savedAgreements?.length) {
      setSaved(await getSavedAgreements(userDoc.savedAgreements));
    }
    setLoading(false);
  }, [user.uid]);

  useEffect(() => { load(); }, [load]);

  const handleView = (a) => {
    if (a.status === 'Pending') navigate(`/pending/${a.agreementID}`);
    else if (a.status === 'Confirmed') navigate(`/agreement-confirmed/${a.agreementID}`);
    else if (a.status === 'Cancelled') navigate(`/agreement-declined/${a.agreementID}`);
    else navigate(`/agreement-declined/${a.agreementID}`);
  };
  const handleArchive = async (id) => { await archiveAgreement(id); load(); };
  const handleRestore = async (id) => { await restoreAgreement(id); load(); };
  const handleRemoveSaved = async (aid) => { await removeSavedAgreement(user.uid, aid); setSaved(p => p.filter(a => a.agreementID !== aid)); };

  const isPaymentType = (t) => ['payment_agreement', 'group_payment_agreement', 'paid_appointment', 'group_paid_appointment'].includes(t);
  const matchesSearch = (a) => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return (a.title||'').toLowerCase().includes(q) || (a.partnerName||'').toLowerCase().includes(q)
      || (a.agreementID||'').toLowerCase().includes(q) || (a.participants||[]).some(p => (p.name||'').toLowerCase().includes(q));
  };
  const matchesType = (a) => paymentsOnly ? isPaymentType(a.type) : true;
  const matchesVerified = (a) => verifiedOnly ? a.verified === true : true;
  const sortList = (list) => {
    const s = [...list];
    if (sortBy === 'az') s.sort((a,b) => (a.title||'').localeCompare(b.title||''));
    else if (sortBy === 'deadline') s.sort((a,b) => (a.deadline ? new Date(a.deadline).getTime() : Infinity) - (b.deadline ? new Date(b.deadline).getTime() : Infinity));
    else s.sort((a,b) => { const aT = a.createdTime?.toDate?.() || new Date(a.createdTime||0); const bT = b.createdTime?.toDate?.() || new Date(b.createdTime||0); return bT - aT; });
    return s;
  };

  const filtered = agreements.filter(a => matchesSearch(a) && matchesType(a) && matchesVerified(a));
  const pending = sortList(filtered.filter(a => a.status === 'Pending' && !a.archived));
  const active = sortList(filtered.filter(a => a.status === 'Confirmed' && !a.archived));
  const declined = sortList(filtered.filter(a => a.status === 'Declined' && !a.archived));
  const archived = sortList(filtered.filter(a => a.archived));
  const filteredSaved = sortList(saved.filter(a => matchesSearch(a) && matchesType(a)));
  const filteredShared = sortList(shared.filter(a => matchesSearch(a) && matchesType(a)));

  if (loading) return <div className="page"><Navbar /><div className="container" style={{ paddingTop: 32 }}>{t('dashboard.loading')}</div></div>;

  return (
    <div className="page">
      <Navbar />
      <div className="container" style={{ paddingTop: 28, paddingBottom: 64 }}>

        {upgradeBanner && (
          <div className="hero hero-premium" style={{ padding: '16px 20px', marginBottom: 16 }}>
            <CircleIcon name="star" color="white" size={36} iconSize={18} style={{ margin: '0 auto 6px', background: 'rgba(255,255,255,0.25)' }} />
            <div className="hero-title" style={{ fontSize: '1.05rem' }}>{t('dashboard.welcomeTier', { tier: upgradeBanner === 'business' ? 'Business' : 'Premium' })}</div>
            <div className="hero-sub" style={{ fontSize: '0.8rem' }}>{t('dashboard.upgraded')}</div>
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h1 style={{ fontSize: '1.4rem' }}>{t('dashboard.myAgreements')}</h1>
          <Link to="/create" className="btn btn-primary btn-sm" style={{ marginBottom: 0 }}>{t('dashboard.new')}</Link>
        </div>

        <input className="form-input" placeholder={t('dashboard.searchPlaceholder')} value={searchQuery} onChange={e => setSearchQuery(e.target.value)} style={{ marginBottom: 10 }} />
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
          <button
            onClick={() => setPaymentsOnly(!paymentsOnly)}
            style={{
              padding: '5px 12px', borderRadius: 20, fontSize: '0.74rem', fontWeight: 600,
              border: `1.5px solid ${paymentsOnly ? 'var(--primary)' : 'var(--border)'}`,
              background: paymentsOnly ? 'var(--primary-light)' : 'var(--surface)',
              color: paymentsOnly ? 'var(--primary)' : 'var(--muted)',
              cursor: 'pointer', fontFamily: 'Noto Sans, sans-serif', transition: 'all 0.15s',
            }}
          >
            {t('dashboard.paymentsOnly')}
          </button>
          <button
            onClick={() => setVerifiedOnly(!verifiedOnly)}
            style={{
              padding: '5px 12px', borderRadius: 20, fontSize: '0.74rem', fontWeight: 600,
              border: `1.5px solid ${verifiedOnly ? 'var(--confirmed)' : 'var(--border)'}`,
              background: verifiedOnly ? 'var(--confirmed-bg)' : 'var(--surface)',
              color: verifiedOnly ? 'var(--confirmed)' : 'var(--muted)',
              cursor: 'pointer', fontFamily: 'Noto Sans, sans-serif', transition: 'all 0.15s',
            }}
          >
            <SvgIcon name="shield" size={11} color={verifiedOnly ? 'var(--confirmed)' : 'var(--muted)'} strokeWidth={2.5}/> {t('dashboard.verifiedOnly')}
          </button>
          <select className="form-select" value={sortBy} onChange={e => setSortBy(e.target.value)} style={{ marginBottom: 0, minWidth: 150, flex: 'none', marginLeft: 'auto' }}>
            <option value="recent">{t('dashboard.recentlyCreated')}</option>
            <option value="az">{t('dashboard.aToZ')}</option>
            <option value="deadline">{t('dashboard.endingSoonest')}</option>
          </select>
        </div>

        <SectionToggle icon={<CircleIcon name="clock" color="var(--pending)" size={28} iconSize={14}/>} title={t('dashboard.pendingConfirmation')} subtitle={t('dashboard.pendingSubtitle')} count={pending.length} expanded={sections.pending} onToggle={() => toggle('pending')}>
          {pending.length === 0 ? <div className="empty-state">{t('dashboard.noPending')}</div> : pending.map(a => <AgreementCard key={a.id} agreement={a} onView={handleView} onArchive={handleArchive} t={t} />)}
        </SectionToggle>

        <SectionToggle icon={<CircleIcon name="check" color="var(--confirmed)" size={28} iconSize={14}/>} title={t('dashboard.activeAgreements')} subtitle={t('dashboard.activeSubtitle')} count={active.length} expanded={sections.active} onToggle={() => toggle('active')}>
          {active.length === 0 ? <div className="empty-state">{t('dashboard.noActive')}</div> : active.map(a => <AgreementCard key={a.id} agreement={a} onView={handleView} onArchive={handleArchive} t={t} />)}
        </SectionToggle>

        <SectionToggle icon={<CircleIcon name="x" color="var(--declined)" size={28} iconSize={14}/>} title={t('dashboard.declined')} count={declined.length} expanded={sections.declined} onToggle={() => toggle('declined')}>
          {declined.length === 0 ? <div className="empty-state">{t('dashboard.noDeclined')}</div> : declined.map(a => <AgreementCard key={a.id} agreement={a} onView={handleView} onArchive={handleArchive} t={t} />)}
        </SectionToggle>

        {filteredShared.length > 0 && (
          <SectionToggle icon={<CircleIcon name="users" color="var(--primary)" size={28} iconSize={14}/>} title={t('dashboard.sharedWithYou')} subtitle={t('dashboard.sharedSubtitle')} count={filteredShared.length} expanded={sections.shared} onToggle={() => toggle('shared')}>
            {filteredShared.map(a => <SharedCard key={a.id} agreement={a} onView={handleView} t={t} />)}
          </SectionToggle>
        )}

        {filteredSaved.length > 0 && (
          <SectionToggle icon={<CircleIcon name="save" color="var(--primary)" size={28} iconSize={14}/>} title={t('dashboard.savedAgreements')} subtitle={t('dashboard.savedSubtitle')} count={filteredSaved.length} expanded={sections.saved} onToggle={() => toggle('saved')}>
            {filteredSaved.map(a => <SavedCard key={a.id} agreement={a} onView={handleView} onRemove={handleRemoveSaved} t={t} />)}
          </SectionToggle>
        )}

        {archived.length > 0 && (
          <SectionToggle icon={<CircleIcon name="archive" color="var(--muted)" size={28} iconSize={14}/>} title={t('dashboard.archived')} count={archived.length} expanded={sections.archived} onToggle={() => toggle('archived')}>
            {archived.map(a => <AgreementCard key={a.id} agreement={a} onView={handleView} onRestore={handleRestore} isArchived t={t} />)}
          </SectionToggle>
        )}

        {!FULL_ACCESS_MODE && !isPremium && (
          <div className="upsell-banner">
            <div className="upsell-banner-title"><SvgIcon name="heart" size={14} color="var(--premium)"/> {t('dashboard.upsell')}</div>
            <Link to="/pricing" className="btn btn-premium" style={{ padding: '9px', fontSize: '0.82rem', marginBottom: 0 }}>
              {t('dashboard.upgradePremium')}
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
